import React, { useState, useCallback } from 'react';
import { Eye, Search, ChevronLeft, ChevronRight, Filter, X, PenLine } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import { getAllSubmissions, type SubmissionRecord } from '@/services/PagesServices/FeedbackManagementService';
import './SubmissionsViewer.css';

interface SubmissionFilter {
  formType?: 'feedback' | 'satisfaction' | 'assessment';
  assessmentType?: 'objective' | 'subjective';
  search: string;
}

const PAGE_SIZE = 20;

const FORM_TYPE_LABELS: Record<string, string> = {
  feedback: 'Feedback',
  satisfaction: 'Satisfaction',
  assessment: 'Assessment',
};

const ASSESSMENT_TYPE_LABELS: Record<string, string> = {
  objective: 'Objective',
  subjective: 'Subjective',
  both: 'Combined',
};

const SubmissionsViewer: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const rolePrefix = pathname.startsWith('/superadmin') ? '/superadmin' : '/admin';
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<SubmissionFilter>({ search: '' });
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['submissions', currentPage, filters],
    queryFn: async () => {
      const result = await getAllSubmissions(
        currentPage, PAGE_SIZE, filters.formType, filters.assessmentType, filters.search
      );
      if (!result.success || !result.data) {
        throw new Error('Failed to fetch submissions');
      }
      return {
        // API returns snake_case — map to camelCase SubmissionRecord
        submissions: (result.data.submissions as unknown as Array<Record<string, unknown>>).map((sub) => ({
          id: sub.id as number,
          formId: sub.form_id as number,
          formName: sub.form_name as string,
          formType: sub.form_type as string,
          assessmentType: sub.assessment_type as string | undefined,
          respondentName: sub.respondent_name as string | null,
          respondentEmail: sub.respondent_email as string | null,
          submittedAt: sub.submitted_at as string,
          score: sub.score as number | null,
          maxScore: sub.max_score as number | null,
          percentage: sub.percentage ? parseFloat(String(sub.percentage)) : null,
        })) as SubmissionRecord[],
        totalPages: result.data.pagination.totalPages as number,
      };
    },
    staleTime: 30 * 1000,
  });

  const submissions = data?.submissions ?? [];
  const totalPages = data?.totalPages ?? 1;

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);

  const handleFilterChange = useCallback((key: keyof SubmissionFilter, value: string) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? undefined : value }));
  }, []);

  const clearFilters = useCallback(() => {
    setCurrentPage(1);
    setSearchInput('');
    setFilters({ search: '' });
  }, []);

  const hasActiveFilters = Boolean(filters.formType || filters.assessmentType || filters.search);

  if (error) return <ErrorDisplay message={error instanceof Error ? error.message : 'Failed to load submissions'} />;

  return (
    <div className="admin-submissions-viewer">
      <div className="admin-submissions-header">
        <h2>All Submissions</h2>
        <p className="admin-submissions-subtitle">View all submissions across all forms</p>
      </div>

      {/* Search and Filter Controls */}
      <div className="admin-submissions-controls">
        <div className="admin-submissions-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="admin-submissions-search-btn" onClick={handleSearch}>Search</button>
        </div>
        <button
          className={`admin-submissions-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} /> Filters
          {hasActiveFilters && <span className="admin-submissions-filter-badge">&bull;</span>}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="admin-submissions-filters">
          <div className="admin-submissions-filter-group">
            <label>Form Type</label>
            <select value={filters.formType ?? 'all'} onChange={(e) => handleFilterChange('formType', e.target.value)}>
              <option value="all">All Types</option>
              <option value="feedback">Feedback</option>
              <option value="satisfaction">Satisfaction</option>
              <option value="assessment">Assessment</option>
            </select>
          </div>
          {filters.formType === 'assessment' && (
            <div className="admin-submissions-filter-group">
              <label>Assessment Type</label>
              <select value={filters.assessmentType ?? 'all'} onChange={(e) => handleFilterChange('assessmentType', e.target.value)}>
                <option value="all">All Assessment Types</option>
                <option value="objective">Objective</option>
                <option value="subjective">Subjective</option>
              </select>
            </div>
          )}
          {hasActiveFilters && (
            <button className="admin-submissions-clear-filters" onClick={clearFilters}>
              <X size={16} /> Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Submissions Table */}
      <div className="admin-submissions-table-container">
        {isLoading ? (
          <LoadingSpinner />
        ) : submissions.length > 0 ? (
          <>
            <SubmissionsTable submissions={submissions} onView={(id) => navigate(`${rolePrefix}/tools/feedback/response/${id}`)} />
            {totalPages > 1 && (
              <div className="admin-submissions-pagination">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="admin-submissions-pagination-btn">
                  <ChevronLeft size={18} />
                </button>
                <span className="admin-submissions-pagination-info">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="admin-submissions-pagination-btn">
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="admin-submissions-empty">
            <p>No submissions found</p>
            {hasActiveFilters && <p className="admin-submissions-empty-hint">Try adjusting your filters</p>}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Table sub-component ---

const SubmissionsTable: React.FC<{
  submissions: SubmissionRecord[];
  onView: (id: number) => void;
}> = React.memo(({ submissions, onView }) => (
  <table className="admin-submissions-table">
    <thead>
      <tr>
        <th>Respondent</th><th>Email</th><th>Form</th><th>Type</th>
        <th>Assessment</th><th>Score</th><th>Submitted</th><th>Action</th>
      </tr>
    </thead>
    <tbody>
      {submissions.map((sub) => (
        <tr key={sub.id} className="admin-submissions-row">
          <td className="admin-submissions-cell-name">{sub.respondentName || 'Anonymous'}</td>
          <td className="admin-submissions-cell-email">{sub.respondentEmail || '-'}</td>
          <td className="admin-submissions-cell-form">{sub.formName}</td>
          <td>
            <span className={`admin-submissions-badge admin-submissions-badge-${sub.formType}`}>
              {FORM_TYPE_LABELS[sub.formType] ?? sub.formType}
            </span>
          </td>
          <td><span className="admin-submissions-assessment">{ASSESSMENT_TYPE_LABELS[sub.assessmentType ?? ''] ?? '-'}</span></td>
          <td className="admin-submissions-score"><ScoreDisplay submission={sub} /></td>
          <td className="admin-submissions-date">{formatDate(sub.submittedAt)}</td>
          <td className="admin-submissions-action">
            {sub.formType === 'assessment' && (sub.assessmentType === 'subjective' || sub.assessmentType === 'both') ? (
              <button className="admin-submissions-score-btn" onClick={() => onView(sub.id)} title="Score this submission">
                <PenLine size={14} /> Score
              </button>
            ) : (
              <button className="admin-submissions-view-btn" onClick={() => onView(sub.id)} title="View submission details">
                <Eye size={16} />
              </button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
));

const ScoreDisplay: React.FC<{ submission: SubmissionRecord }> = ({ submission }) => {
  const { score, maxScore, percentage, formType, assessmentType } = submission;
  if (score != null && maxScore != null) {
    return <span>{score}/{maxScore} ({percentage?.toFixed(1)}%)</span>;
  }
  if (formType === 'assessment' && (assessmentType === 'subjective' || assessmentType === 'both')) {
    return <span className="admin-submissions-needs-scoring">Needs Scoring</span>;
  }
  return <span>-</span>;
};

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default SubmissionsViewer;
